<template>
  <div class="select-list-wrapper">
    <div class="row">
      <div v-if="selectLabel" class="col-md-12 mb-2">
        <label class="select-label" :for="resolvedSelectInputId">{{
          selectLabel
        }}</label>
      </div>
      <div class="col-md-10">
        <LazyLoadSelect
          data-test-id="add-favorites-select-list"
          :selected="state.selectedData"
          :input-id="resolvedSelectInputId"
          :accessible-label="selectLabel || selectPlaceholder || undefined"
          :placeholder="selectPlaceholder"
          :type="selectType"
          :exclude-options="state.data"
          :close-after-select="closeAfterSelect"
          @update:model-value="
            (value) => {
              pushData(value)
            }
          "
          :invalid-feedback="requiredFeedback"
        ></LazyLoadSelect>
      </div>
      <div
        class="selected-counter col-md-2"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        :aria-label="counterStatusLabel"
      >
        <span aria-hidden="true">{{ state.data.length }}</span>
        <span v-if="counterMax" class="selected-counter-max" aria-hidden="true"
          ><span>/</span>{{ counterMax }}</span
        >
        <span :class="counterIconClass ? counterIconClass : 'circle'">
          <i
            :class="counterIcon"
            :role="counterIconAriaLabel ? 'img' : undefined"
            :aria-label="counterIconAriaLabel || undefined"
            :aria-hidden="counterIconAriaLabel ? undefined : 'true'"
          ></i>
        </span>
      </div>
    </div>
    <div v-if="infoText" class="info-box mt-4">
      <InfoBox>{{ infoText }}</InfoBox>
    </div>
    <div
      v-if="activeColumnNames"
      class="column-name d-flex justify-content-between mt-3"
    >
      <div v-if="firstColumnName && state.data.length">
        {{ firstColumnName }}
      </div>
      <div v-if="lastColumnName && state.data.length" class="column-name-last">
        {{ lastColumnName }}
      </div>
    </div>
    <div v-if="state.data.length" class="list">
      <draggable
        :disabled="!draggable"
        v-model="state.data"
        item-key="id"
        @end="dragging = dragData()"
      >
        <template #item="{ element, index }">
          <div
            :key="element + 'tg-2'"
            class="data-row"
            :style="draggable ? 'cursor:pointer;' : ''"
            :id="'row-' + element.value.id"
          >
            <div class="d-flex align-items-center">
              <div class="d-flex align-items-center">
                <div
                  v-if="draggable"
                  class="icon-draggable d-flex align-items-center px-2 py-1 rounded gap-2"
                >
                  <i class="ri-drag-move-fill" aria-hidden="true"></i>
                  <IconButton
                    :icon="'ri-arrow-up-s-line'"
                    :disabled="index === 0"
                    :aria-label="'Move item up'"
                    :tooltip="'Move item up'"
                    @click="moveUp(index)"
                  />
                  <IconButton
                    :icon="'ri-arrow-down-s-line'"
                    :disabled="index === state.data.length - 1"
                    :aria-label="'Move item down'"
                    :tooltip="'Move item down'"
                    @click="moveDown(index)"
                  />
                </div>
                <div v-if="favoriteMode" class="icon-image">
                  <div v-if="element.value.type == 'App\\Models\\Room'">
                    <i
                      v-if="element.icon"
                      :class="element.icon"
                      aria-hidden="true"
                    ></i>
                    <i v-else class="flaticon-star" aria-hidden="true"></i>
                  </div>
                  <div v-else>
                    <img
                      v-if="element.avatar"
                      :src="element.avatar"
                      :alt="`${element.label || 'User'} avatar`"
                    />
                    <img
                      v-else
                      src="/img/avatars/user-avatar.jpeg"
                      :alt="`${element.label || 'User'} avatar`"
                    />
                  </div>
                </div>
              </div>
              <span>{{ element.label }}</span>
            </div>
            <div class="d-flex">
              <div v-if="activeFavoriteRoomPin" class="d-flex">
                <InputField
                  :id="`${resolvedSelectInputId}-pin-${element.value.id}`"
                  placeholder="PIN"
                  max-length="6"
                  :input-aria-label="`PIN for ${element.label}`"
                  v-model="element.pin"
                  :disabled="!element.isDisabled"
                  class="mx-2"
                  :invalid-feedback="pinFieldInvalidFeedback(index)"
                ></InputField>
                <IconButton
                  :icon="'ri-edit-2-line'"
                  :aria-label="`Edit PIN for ${element.label}`"
                  @click="editPin(element)"
                  class="action-edit-pin"
                />
              </div>
              <div>
                <IconButton
                  data-test-id="remove-favorites-select-list"
                  :icon="'ri-delete-bin-line'"
                  :aria-label="`Remove ${element.label}`"
                  @click="removeData(element.value.id)"
                  class="action-delete"
                />
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>
    <div
      v-if="Object.keys(nonPinFieldInvalidFeedback).length !== 0"
      class="error-list"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div
        v-for="(errors, errorIndex) in nonPinFieldInvalidFeedback"
        :key="errorIndex"
        class="invalid-message text-start"
      >
        <p v-for="(label, index) in state.data" :key="index">
          <span
            v-if="
              'user_ids.' + index == errorIndex ||
              'room_ids.' + index == errorIndex ||
              'autopass_rooms.' + index == errorIndex
            "
            >{{ label.label }}
            <span
              v-for="(errorMessages, emIdx) in errors"
              :key="emIdx"
              class="errorMessageCase"
            >
              {{ errorMessages }}
            </span>
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, watch, onMounted, getCurrentInstance } from "vue"
import { useI18n } from "vue-i18n"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import IconButton from "../Buttons/IconButton.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import draggable from "vuedraggable"

export default {
  name: "SelectList",
  components: {
    LazyLoadSelect,
    IconButton,
    InfoBox,
    InputField,
    draggable
  },
  props: {
    selectInputId: {
      type: String,
      default: ""
    },
    data: {
      type: Array,
      default: () => []
    },
    favoriteMode: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    selectLabel: {
      type: String,
      default: ""
    },
    selectPlaceholder: {
      type: String,
      default: ""
    },
    selectType: {
      type: String,
      default: ""
    },
    invalidFeedback: {
      type: Object,
      default: () => ({})
    },
    requiredFeedback: {
      type: String,
      default: ""
    },
    reduce: {
      type: Function,
      default: (item) => item
    },
    counterMax: {
      type: Number,
      default: null
    },
    counterIcon: {
      type: String,
      default: ""
    },
    counterIconAriaLabel: {
      type: String,
      default: ""
    },
    counterIconClass: {
      type: String,
      default: "circle"
    },
    infoText: {
      type: String,
      default: ""
    },
    firstColumnName: {
      type: String,
      default: ""
    },
    lastColumnName: {
      type: String,
      default: ""
    },
    activeFavoriteRoomPin: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Function
    },
    closeAfterSelect: {
      type: Boolean,
      default: true
    }
  },
  emits: ["updateListData", "selectedElement"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const resolvedSelectInputId = computed(() => {
      return props.selectInputId?.trim()
        ? props.selectInputId
        : `selectlist-${getCurrentInstance()?.uid || "input"}`
    })
    const state = reactive({
      data: [],
      selectedData: [],
      form: {
        pin: null
      },
      disabledPinField: true
    })

    onMounted(() => {
      if (props.data?.length) {
        state.data = props.data
      }
    })

    watch(
      () => props.data,
      (value) => {
        if (value) {
          state.data = value
        }
      }
    )

    const activeColumnNames = computed(() => {
      return props.firstColumnName || props.lastColumnName
    })

    const PIN_ERROR_KEY_RE = /^room_ids_and_pins\.\d+\.pin$/

    /** Server validation for assign-locations PINs — surfaced on each row's InputField (aria-describedby). */
    const pinFieldInvalidFeedback = (index) => {
      const fb = props.invalidFeedback
      if (!fb || typeof fb !== "object") return ""
      const key = `room_ids_and_pins.${index}.pin`
      const val = fb[key]
      if (val == null || val === "") return ""
      return Array.isArray(val) ? val[0] : String(val)
    }

    const nonPinFieldInvalidFeedback = computed(() => {
      const raw = props.invalidFeedback
      if (!raw || typeof raw !== "object") return {}
      return Object.fromEntries(
        Object.entries(raw).filter(([k]) => !PIN_ERROR_KEY_RE.test(k))
      )
    })

    const counterStatusLabel = computed(() =>
      t("common.a11y_locations_selected_count", {
        count: state.data.length
      })
    )

    const pushData = (value) => {
      if (props.counterMax) {
        if (state.data.length < props.counterMax) {
          state.data.push(value)
          state.selectedData = value
          emit("selectedElement", value.value.id)
          sendData()
        }
      } else {
        state.data.push(value)
        state.selectedData = value
        emit("selectedElement", value.value.id)
        sendData()
      }
      setTimeout(() => {
        state.selectedData = []
      }, 500)
    }

    const dragData = () => {
      sendData()
    }
    const editPin = (element) => {
      element.isDisabled = !element.isDisabled
    }

    const removeData = (dataId) => {
      state.data = state.data.filter((e) => e.value.id !== dataId)
      sendData()
    }

    const moveUp = (index) => {
      if (index > 0) {
        ;[state.data[index], state.data[index - 1]] = [
          state.data[index - 1],
          state.data[index]
        ]
        sendData()
      }
    }

    const moveDown = (index) => {
      if (index < state.data.length - 1) {
        ;[state.data[index], state.data[index + 1]] = [
          state.data[index + 1],
          state.data[index]
        ]
        sendData()
      }
    }

    const sendData = () => {
      emit("updateListData", state.data.map(props.reduce))
    }

    return {
      state,
      props,
      activeColumnNames,
      resolvedSelectInputId,
      pinFieldInvalidFeedback,
      nonPinFieldInvalidFeedback,
      counterStatusLabel,
      pushData,
      dragData,
      editPin,
      removeData,
      moveUp,
      moveDown,
      sendData
    }
  }
}
</script>
